import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Dialog } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Plus, Calendar, Edit2, Trash2, X, Globe, MapPin, Building2, Star } from "lucide-react";
import styles from "../hrms.module.scss";
import { APIConstant } from "services/api-constant";
import Popup from "components/minor/popup/popup";
import { useTheme } from "context/ThemeContext";

const Schema = Yup.object({
  name: Yup.string().required("Required"),
  date: Yup.string().required("Required"),
  type: Yup.string().required("Required"),
});

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const today = new Date().toISOString().split("T")[0];

export default function HolidayCalendar() {
  const { T } = useTheme();
  const TYPE_CONFIG = {
    National: { color:T.red,   icon:Globe,     bg:`${T.red}1F`   },
    Regional: { color:T.amber, icon:MapPin,    bg:`${T.amber}1F`  },
    Optional: { color:T.muted, icon:Star,      bg:`${T.muted}1F`  },
    Company:  { color:T.cyan,  icon:Building2, bg:`${T.cyan}1F`   },
  };

  const currentYear = new Date().getFullYear();
  const [year,    setYear]    = useState(currentYear);
  const [holidays,setHolidays]= useState([]);
  const [loading, setLoading] = useState(true);
  const [open,    setOpen]    = useState(false);
  const [mode,    setMode]    = useState("add");
  const [sel,     setSel]     = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [delId,   setDelId]   = useState(null);

  const fetchHolidays = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${APIConstant.holidays}?year=${year}`);
      setHolidays(r.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchHolidays(); }, [year]);

  const handleSubmit = async (vals) => {
    try {
      if (mode==="edit"&&sel) await axios.put(`${APIConstant.holidays}${sel.id}`, vals);
      else await axios.post(APIConstant.holidays, vals);
      setOpen(false); fetchHolidays();
    } catch(e) { console.error(e); }
  };

  const handleDelete = async () => {
    try { await axios.delete(`${APIConstant.holidays}${delId}`); setDelOpen(false); fetchHolidays(); }
    catch(e) { console.error(e); }
  };

  // Group by month
  const byMonth = useMemo(() => {
    const g = {};
    holidays.forEach(h => {
      const m = parseInt(h.date.split("-")[1]) - 1;
      if (!g[m]) g[m] = [];
      g[m].push(h);
    });
    return g;
  }, [holidays]);

  const isPast = (d) => d < today;

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>TIME & ATTENDANCE</div>
          <h2>Holiday Calendar</h2>
          <p>Company holidays, national holidays and optional leaves</p>
        </div>
        <div className={styles.headerRight}>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <button onClick={()=>setYear(p=>p-1)} style={{background:T.inp_bg,border:`1px solid ${T.border}`,borderRadius:7,color:T.text,padding:"7px 12px",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13}}>‹</button>
            <span style={{fontFamily:"JetBrains Mono,monospace",fontSize:16,fontWeight:700,color:T.cyan,minWidth:50,textAlign:"center"}}>{year}</span>
            <button onClick={()=>setYear(p=>p+1)} style={{background:T.inp_bg,border:`1px solid ${T.border}`,borderRadius:7,color:T.text,padding:"7px 12px",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13}}>›</button>
          </div>
          <button className={styles.btnPrimary} onClick={()=>{setMode("add");setSel(null);setOpen(true);}}>
            <Plus size={14}/> Add Holiday
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div style={{display:"flex",gap:12,marginBottom:24,flexWrap:"wrap"}}>
        {Object.entries(TYPE_CONFIG).map(([type,{color,icon:Icon,bg}])=>{
          const cnt = holidays.filter(h=>h.type===type).length;
          return (
            <div key={type} style={{display:"flex",alignItems:"center",gap:10,background:T.toggle_bg,border:`1px solid ${color}40`,borderRadius:10,padding:"12px 18px",flex:1,minWidth:140}}>
              <div style={{width:36,height:36,borderRadius:9,background:bg,display:"flex",alignItems:"center",justifyContent:"center",color}}>
                <Icon size={16}/>
              </div>
              <div>
                <div style={{fontSize:20,fontWeight:800,fontFamily:"JetBrains Mono,monospace",color}}>{cnt}</div>
                <div style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em"}}>{type}</div>
              </div>
            </div>
          );
        })}
        <div style={{display:"flex",alignItems:"center",gap:10,background:T.toggle_bg,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 18px",flex:1,minWidth:140}}>
          <div style={{width:36,height:36,borderRadius:9,background:`${T.cyan}1A`,display:"flex",alignItems:"center",justifyContent:"center",color:T.cyan}}>
            <Calendar size={16}/>
          </div>
          <div>
            <div style={{fontSize:20,fontWeight:800,fontFamily:"JetBrains Mono,monospace",color:T.cyan}}>{holidays.length}</div>
            <div style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em"}}>Total {year}</div>
          </div>
        </div>
      </div>

      {/* Month grid */}
      {loading ? <div className={styles.loader}><div className={styles.spinner}/>Loading…</div> : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16}}>
          {MONTHS.map((month, mi) => {
            const mHolidays = byMonth[mi] || [];
            const isCurrentMonth = (new Date().getMonth() === mi && year === currentYear);
            return (
              <div key={month} className={styles.card} style={{border:isCurrentMonth?`1px solid ${T.cyan}33`:`1px solid ${T.cyan}12`}}>
                <div style={{padding:"14px 18px",borderBottom:`1px solid ${T.cyan}12`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <h4 style={{margin:0,fontSize:14,fontWeight:600,color:isCurrentMonth?T.cyan:T.text}}>{month} {year}</h4>
                  <span style={{fontSize:11.5,fontFamily:"JetBrains Mono,monospace",color:T.muted}}>{mHolidays.length}</span>
                </div>
                <div style={{padding:"10px 18px"}}>
                  {mHolidays.length===0 ? (
                    <div style={{textAlign:"center",padding:"16px 0",color:T.dim,fontSize:12}}>No holidays</div>
                  ) : mHolidays.map(h => {
                    const cfg = TYPE_CONFIG[h.type] || TYPE_CONFIG.National;
                    const TypeIcon = cfg.icon;
                    const past = isPast(h.date);
                    const day = new Date(h.date).toLocaleDateString("en-IN",{weekday:"short",day:"2-digit"});
                    return (
                      <div key={h.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:`1px solid ${T.cyan}0A`,opacity:past?0.5:1}}>
                        <div style={{width:32,height:32,borderRadius:8,background:cfg.bg,display:"flex",alignItems:"center",justifyContent:"center",color:cfg.color,flexShrink:0}}>
                          <TypeIcon size={13}/>
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12.5,fontWeight:500,color:past?T.muted:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{h.name}</div>
                          <div style={{fontSize:11,color:T.dim}}>{day}</div>
                        </div>
                        <div style={{display:"flex",gap:4}}>
                          <button style={{background:"none",border:"none",cursor:"pointer",color:T.dim,padding:4,borderRadius:5,transition:"color 0.15s"}}
                            onMouseEnter={e=>e.target.style.color=T.cyan}
                            onMouseLeave={e=>e.target.style.color=T.dim}
                            onClick={()=>{setMode("edit");setSel(h);setOpen(true);}}>
                            <Edit2 size={11}/>
                          </button>
                          <button style={{background:"none",border:"none",cursor:"pointer",color:T.dim,padding:4,borderRadius:5,transition:"color 0.15s"}}
                            onMouseEnter={e=>e.target.style.color=T.red}
                            onMouseLeave={e=>e.target.style.color=T.dim}
                            onClick={()=>{setDelId(h.id);setDelOpen(true);}}>
                            <Trash2 size={11}/>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Holiday list table */}
      <div className={styles.card} style={{marginTop:20}}>
        <div className={styles.cardHeader}><h3>All Holidays — {year}</h3><span className={styles.cardMeta}>{holidays.length} holidays</span></div>
        <div className={styles.tableWrap}>
          <table className={styles.darkTable}>
            <thead><tr><th>Holiday</th><th>Date</th><th>Day</th><th>Type</th><th>Actions</th></tr></thead>
            <tbody>
              {holidays.length===0 ? (
                <tr className={styles.emptyRow}><td colSpan={5}>No holidays for {year}</td></tr>
              ) : holidays.map(h => {
                const d = new Date(h.date);
                const cfg = TYPE_CONFIG[h.type] || TYPE_CONFIG.National;
                return (
                  <tr key={h.id} style={{opacity:isPast(h.date)?0.6:1}}>
                    <td style={{fontWeight:500}}>{h.name}</td>
                    <td style={{fontFamily:"JetBrains Mono,monospace",fontSize:12,color:T.cyan}}>{h.date}</td>
                    <td className={styles.dim}>{d.toLocaleDateString("en-IN",{weekday:"long"})}</td>
                    <td>
                      <span style={{fontSize:11.5,padding:"3px 9px",borderRadius:20,background:cfg.bg,color:cfg.color,fontWeight:600}}>{h.type}</span>
                    </td>
                    <td>
                      <div style={{display:"flex",gap:6}}>
                        <button className={styles.btnSm} onClick={()=>{setMode("edit");setSel(h);setOpen(true);}}><Edit2 size={12}/> Edit</button>
                        <button className={styles.btnSm} style={{color:T.red,borderColor:`${T.red}40`}}
                          onClick={()=>{setDelId(h.id);setDelOpen(true);}}><Trash2 size={12}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form Modal */}
      <Dialog open={open} onClose={()=>setOpen(false)}>
        <div style={{background:T.card,border:`1px solid ${T.cyan}33`,borderRadius:14,width:460,fontFamily:"Outfit,sans-serif",overflow:"hidden"}}>
          <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.border}`,background:`${T.cyan}0A`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <h3 style={{margin:0,fontSize:16,fontWeight:600,color:T.text}}>{mode==="add"?"Add":"Edit"} Holiday</h3>
            <button style={{background:"none",border:"none",cursor:"pointer",color:T.muted}} onClick={()=>setOpen(false)}><X size={16}/></button>
          </div>
          <div style={{padding:24}}>
            <Formik
              enableReinitialize
              initialValues={mode==="edit"&&sel
                ? {name:sel.name,date:sel.date,type:sel.type,description:sel.description||""}
                : {name:"",date:"",type:"National",description:""}}
              validationSchema={Schema}
              onSubmit={handleSubmit}
            >
              {({errors,touched})=>(
                <Form style={{display:"flex",flexDirection:"column",gap:14}}>
                  {[
                    {name:"name",label:"Holiday Name *",placeholder:"e.g. Republic Day"},
                    {name:"date",label:"Date *",type:"date"},
                  ].map(({name,label,placeholder,type="text"})=>(
                    <div key={name}>
                      <label style={{fontSize:11.5,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</label>
                      <Field name={name} type={type} placeholder={placeholder}
                        style={{width:"100%",background:T.inp_bg,border:`1px solid ${errors[name]&&touched[name]?T.red:T.border}`,borderRadius:8,color:T.text,fontSize:13,padding:"9px 12px",outline:"none",fontFamily:"Outfit,sans-serif",boxSizing:"border-box"}}/>
                      {errors[name]&&touched[name]&&<p style={{color:T.red,fontSize:11.5,marginTop:4}}>{errors[name]}</p>}
                    </div>
                  ))}
                  <div>
                    <label style={{fontSize:11.5,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.06em"}}>Type *</label>
                    <Field as="select" name="type"
                      style={{width:"100%",background:T.inp_bg,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:13,padding:"9px 12px",outline:"none",fontFamily:"Outfit,sans-serif"}}>
                      {Object.keys(TYPE_CONFIG).map(t=><option key={t} style={{background:T.opt_bg}}>{t}</option>)}
                    </Field>
                  </div>
                  <div>
                    <label style={{fontSize:11.5,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.06em"}}>Description</label>
                    <Field as="textarea" name="description" rows={2} placeholder="Optional note"
                      style={{width:"100%",background:T.inp_bg,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:13,padding:"9px 12px",outline:"none",fontFamily:"Outfit,sans-serif",resize:"vertical",boxSizing:"border-box"}}/>
                  </div>
                  <div style={{display:"flex",gap:10,justifyContent:"flex-end",paddingTop:8,borderTop:`1px solid ${T.border}`}}>
                    <button type="button" onClick={()=>setOpen(false)} className={styles.btnSecondary}>Cancel</button>
                    <button type="submit" className={styles.btnPrimary}>{mode==="add"?"Add Holiday":"Update"}</button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </Dialog>

      <Popup open={delOpen} message="Delete this holiday?" handleClose={()=>setDelOpen(false)} handleConfirm={handleDelete}/>
    </div>
  );
}
